import { Icon } from '@iconify/react'
import { observable, observe } from '@legendapp/state'
import { Computed, useObservable } from '@legendapp/state/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createFileRoute,
  useLoaderData,
} from '@tanstack/react-router'
import { createColumnHelper } from '@tanstack/react-table'
import { useEffect } from 'react'
import { toast } from 'react-toastify'

import { deleteFeatures, fetchFeatureList } from '~features/features/api'
import { Features } from '~features/features/types'
import {
  Button,
  Card,
  CardContent,
  DialogComponent,
  DropdownMenuComponent,
} from '~in-app/components'
import { Option } from '~in-app/components/MultiSelectDropdown'
import {
  FilterDropdownProps,
  TableContextProvider,
  TableState$,
} from '~in-app/components/table/TableContext'
import { auth$ } from '~in-app/legend-state'
import { fetchProductList } from '~in-app/shareable/index.api'
import { ListingResponse } from '~in-app/types/api.dto'

export const Route = createFileRoute('/console/products/features/list')({
  component: FeaturePage,
  loader: async ({ abortController }) => {
    let productIds: Option[] = []
    let parentIds: Option[] = []
    const privilegeValue = auth$.privileges.get()

    const productList = await fetchProductList({
      abortCtrl: abortController,
    })
    productIds = productList?.list.map((item: any) => ({
      value: item.id,
      label: item.name,
    }))
    const defaultFeaturesListing = privilegeValue.has('0.3.8.4')
      ? await fetchFeatureList({
        abortCtrl: abortController,
        query: {
          page: (() => {
            const match = new URLSearchParams(window.location.search).get('page')
            return match ? parseInt(match, 10) - 1 : 0
          })(),
          size: (() => {
            const match = new URLSearchParams(window.location.search).get('pageSize')
            return match ? parseInt(match, 10) : 20
          })(),
          sort: (defaultState$.sortList.get() || [])
            .map(item => `${item.id},${item.desc ? 'desc' : 'asc'}`)
            .join(','),
        },
      })
      : ({} as ListingResponse<Features>)

    // const parentIdSet = new Set(defaultFeaturesListing?.list?.map(item => item.parent).filter(Boolean))
    // parentIds = Array.from(parentIdSet).map((parentId) => {
    //   const parentItem = defaultFeaturesListing?.list.find(item => item.parent === parentId)
    //   return { value: parentId, label: parentItem?.name }
    // })
    return { productIds, privilegeValue, defaultFeaturesListing }
  },
  staticData: {
    privileges: ['0.3.8.4'],
  },
})
const columnHelper = createColumnHelper<Features>()
const defaultState$ = observable<{
  sortList: { id: string, desc: boolean }[]
  pagination: number
  pageSize: number
  queries: string
  productIds: string[] | undefined
  parentIds: Option[] | undefined
  isChange: boolean
  deleteFeatureId: string | undefined
  isOpenDeleteFeature: boolean
  deleteFeatureName: string | undefined
}>({
  sortList: [{ id: 'name', desc: true }],
  pagination: 0,
  pageSize: 20,
  queries: '',
  productIds: undefined,
  parentIds: undefined,
  isChange: false,
  deleteFeatureId: undefined,
  isOpenDeleteFeature: false,
  deleteFeatureName: undefined,
})

function FeaturePage() {
  const { productIds, privilegeValue, defaultFeaturesListing }
    = useLoaderData({
      from: '/console/products/features/list',
    })
  const navigate = Route.useNavigate()

  const dropdownItems = (value: Features) => {
    return [
      {
        label: 'Edit',
        onClick: () =>
          navigate({ to: `/console/products/features/edit/${value.id}` }),
        disabled: !privilegeValue?.has('0.3.8.6'),
        icon: (
          <Icon
            height="16"
            icon="lucide:pencil"
            width="16"
          />
        ),
      },
      {
        label: 'Delete',
        onClick: () => handleDeleteFeature(value),
        disabled: !privilegeValue?.has('0.3.8.7'),
        icon: (
          <Icon
            height="16"
            icon="lucide:trash-2"
            width="16"
          />
        ),
      },
    ]
  }

  const columns = [
    columnHelper.accessor('id', {
      cell: info => info.getValue(),
      header: () => <span>ID</span>,
    }),
    columnHelper.accessor('name', {
      cell: info => info.getValue(),
      header: () => <span>Feature Name</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('code', {
      cell: info => info.getValue(),
      header: () => <span>Feature Code</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('product.name', {
      cell: info => info.getValue(),
      header: () => <span>Product</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('remarks', {
      cell: info => info.getValue() || '-',
      header: () => <span>Remarks</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('desp', {
      cell: info => info.getValue() || '-',
      header: () => <span>Description</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('active', {
      cell: info => info.getValue() ? 'Active' : 'Inactive',
      header: () => <span>Status</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('type', {
      cell: (info) => {
        const value = info.getValue()
        if (value === 'P') return 'Privilege'
        if (value === 'V') return 'Value'
        if (value === 'B') return 'Boolean'
        return '-'
      },
      header: () => <span>Type</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('action', {
      cell: info => (
        <span>
          <DropdownMenuComponent
            preventClose
            buttonIcon={<Icon icon="lucide:ellipsis-vertical" />}
            className="rounded-md bg-transparent p-2 hover:opacity-100"
            items={dropdownItems(info.row.original)}
          />
        </span>
      ),
      header: () => <span />,
      enableSorting: false,
      size: 50,
    }),
  ]
  const state$ = useObservable(defaultState$)
  useEffect(() => {
    state$.pagination.set(
      (() => {
        const match = new URLSearchParams(window.location.search).get('page')
        return match ? parseInt(match, 10) - 1 : 0
      })(),
    )
    state$.pageSize.set(
      (() => {
        const match = new URLSearchParams(window.location.search).get('pageSize')
        return match ? parseInt(match, 10) : 20
      })(),
    )
    const filteredQueries = new URLSearchParams(window.location.search).get('queries') || ''
    state$.productIds.set(new URLSearchParams(window.location.search).get('productIds')?.split(','))
    state$.queries.set(filteredQueries)
    const disposer = observe(
      state$,
      (e) => {
        if (e.previous !== undefined && e.value !== undefined) {
          refetch()
        }
      },
      { immediate: false },
    )

    return disposer
  }, [])

  const filterDropdownItems: FilterDropdownProps[] = [
    {
      label: 'Search', // New dropdown for controlling sidebar
      type: 'text',
      result: new URLSearchParams(window.location.search).get('queries') || '',
      value: new URLSearchParams(window.location.search).get('queries') || '',
    },
    // {
    //   label: 'Parents',
    //   type: 'dropdown',
    //   isMultiSelect: true,
    //   options: parentIds,
    //   value: [],
    // },
    {
      label: 'Product',
      type: 'dropdown',
      isMultiSelect: true,
      options: productIds,
      value: productIds.filter(products => new URLSearchParams(window.location.search).get('productIds')?.split(',').includes(products.value)),
      result: productIds.filter(products => new URLSearchParams(window.location.search).get('productIds')?.split(',').includes(products.value)),
    },
  ]
  const tableState$ = useObservable<TableState$<Features>>(() => ({
    id: 'features-table',
    isSortable: true,
    data: [],
    columns: columns,
    hideColumns: ['id'],
    sort: state$.sortList.get(),
    totalItem: 0,
    totalPage: 0,
    pageSize: state$.pageSize.get(),
    pagination: state$.pagination.get(),
    pageIndex: 0,
    filters: filterDropdownItems,
    onApply: (value: any) => {
      const searchParams: Record<string, any> = {}
      const textFilter = value.find((item: any) => item.label === 'Search')
      const textValue = textFilter?.result || ''
      state$.queries.set(textValue)
      searchParams.queries = textValue

      // const parentFilter = value.find(
      //   (item: Option) => item.label === 'Parents',
      // )
      // const parentValue = parentFilter?.result || []
      // if (parentValue.length > 0) {
      //   state$.parentIds.set(
      //     parentValue.map((item: { value: any }) => item.value),
      //   )
      // }
      // else {
      //   state$.parentIds.set(undefined)
      // }
      const productFilter = value.find(
        (item: Option) => item.label === 'Product',
      )
      const productValue = productFilter?.result || []
      if (productValue.length > 0) {
        state$.productIds.set(
          productValue.map((item: { value: any }) => item.value),
        )
        searchParams.productIds = productValue.map((item: { value: any }) => item.value).join(',')
      }
      else {
        state$.productIds.set(undefined)
        searchParams.productIds = undefined
      }
      state$.isChange.set(!state$.isChange.get())
      navigate({
        search: (prev) => {
          const updatedSearch: Record<string, any> = { ...prev, ...searchParams } // Explicitly type as Record<string, any>

          // Remove empty or undefined keys safely
          if (!textFilter?.result) delete updatedSearch.queries
          if (!(productValue.length > 0)) delete updatedSearch.productIds

          return updatedSearch
        },
      })
    },
    setSort: (value: any) => {
      state$.sortList.set(value)
    },
    setPageSize: (value: any) => {
      state$.pageSize.set(value)
      navigate({ search: prev => ({ ...prev, pageSize: value }) })
    },
    setPagination: (value: any) => {
      state$.pagination.set(value)
      navigate({ search: prev => ({ ...prev, page: value + 1 }) })
    },
  }))

  const useDeleteFeature = () => {
    const mutation = useMutation({
      mutationFn: deleteFeatures,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['feature-listing'] })
        toast.success('Features deleted successfully', {
          position: 'top-center',
          hideProgressBar: false,
          autoClose: 1000,
        })
      },
      onError: (error: Error) => {
        console.error('Error occurred:', error)
      },
    })

    return mutation
  }
  const deleteMutation = useDeleteFeature()

  const handleDeleteFeature = (value: Features) => {
    state$.deleteFeatureId.set(value.id)
    state$.deleteFeatureName.set(value.name)
    state$.isOpenDeleteFeature.set(true)
  }

  const queryClient = useQueryClient()

  const { refetch } = useQuery({
    queryKey: ['feature-listing'],
    queryFn: async ({ signal }) => {
      const response = await fetchFeatureList({
        // abortCtrl: signal,
        query: {
          page: state$.pagination.get() || 0,
          size: state$.pageSize.get() || 20,
          queries: state$.queries.get() || undefined,
          productIds: state$?.productIds?.get()
            ? (state$?.productIds?.get() ?? []).join(',')
            : undefined,
          parentIds: state$?.parentIds?.get()
            ? (state$?.parentIds?.get() ?? []).join(',')
            : undefined,
          sort: state$.sortList
            .get()
            .map((sortByVal) => {
              return `${sortByVal.id},${sortByVal.desc ? 'desc' : 'asc'}`
            })
            .join(','),
        },
      })
      const newFeatureList = response?.list.map((item: any) => ({
        ...item,
        action: '',
      })) as Features[]
      tableState$.data.set(newFeatureList || [])
      tableState$.totalItem.set(response?.totalItem || 0)
      tableState$.totalPage.set(response?.totalPage || 0)
      if ((response?.totalPage ?? 0) < (state$.pagination.get() + 1)) {
        state$.pagination.set(0)
        navigate({ search: prev => ({ ...prev, page: 1 }) })
      }
      return response
    },
    initialData: defaultFeaturesListing,
  })
  return (
    <div>
      <div className="flex items-center justify-between p-2">
        <span className="mb-4 ml-4 text-2xl font-bold"> Features Page</span>

        <Computed>
          {
            () => (
              <DialogComponent
                isOpen={state$.isOpenDeleteFeature.get()}
                submitButtonLabel="Delete"
                title={`Are You Sure To Delete this '${state$.deleteFeatureName.get()}' Feature?`}
                onClose={() => { state$.isOpenDeleteFeature.set(false), state$.deleteFeatureName.set(undefined), state$.deleteFeatureId.set(undefined) }}
                onSubmit={
                  () => {
                    const deleteFeatureId = state$.deleteFeatureId.get()
                    if (deleteFeatureId) {
                      deleteMutation.mutate(deleteFeatureId as string)
                      state$.deleteFeatureId.set(undefined)
                      state$.isOpenDeleteFeature.set(false)
                      state$.deleteFeatureName.set(undefined)
                    }
                  }
                }
              />
            )
          }
        </Computed>

        <Button
          className="rounded-md border border-slate-400 bg-transparent font-medium text-muted-foreground hover:opacity-100"
          color="muted"
          disabled={!privilegeValue.has('0.3.8.5')}
          onClick={() => navigate({ to: '/console/products/features/add' })}
        >
          Add
        </Button>
      </div>

      <div className="p-2">
        <Card className="mt-4">
          <CardContent>
            <div>
              <TableContextProvider state$={tableState$} />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
